import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import MemberFormModal from '../../components/members/MemberFormModal';
import type { Member } from "../../models"; // Assuming Member type now includes phoneNumber2 and membershipStatus

export const MembersListPage: React.FC = () => {
    const { user } = useAuth(); // Get logged-in user details for role-based access
    const [members, setMembers] = useState<Member[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [totalMembers, setTotalMembers] = useState<number>(0);
    const [limit] = useState<number>(10); // Number of items per page

    // State for modals
    const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
    const [memberToEdit, setMemberToEdit] = useState<Member | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
    const [memberToDelete, setMemberToDelete] = useState<Member | null>(null);

    // useCallback to memoize fetchMembers to prevent unnecessary re-renders/infinite loops
    const fetchMembers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // The churchIdentifier and Authorization token are expected to be handled by axiosConfig.ts interceptors.
            const response = await api.get(`/members?page=${currentPage}&limit=${limit}`);
            setMembers(response.data.members);
            setCurrentPage(response.data.currentPage);
            setTotalPages(response.data.totalPages);
            setTotalMembers(response.data.totalMembers);
        } catch (err: any) {
            console.error("Failed to fetch members:", err);
            setError(err.response?.data?.message || err.message || 'An error occurred while fetching members.');
        } finally {
            setLoading(false);
        }
    }, [currentPage, limit]); // Dependencies for useCallback

    // useEffect to trigger data fetch on component mount or when page/limit/user changes
    useEffect(() => {
        if (user) { // Only fetch if user is logged in
            fetchMembers();
        }
    }, [user, fetchMembers]); // Dependencies for useEffect

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    // --- CRUD Operation Handlers ---

    // Handles both Add and Edit submission from MemberFormModal
    const handleSaveMember = async (memberData: any, memberId?: string) => {
        try {
            if (memberId) {
                // This is an update operation
                await api.put(`/members/${memberId}`, memberData);
            } else {
                // This is a create new member operation
                await api.post('/members', memberData);
            }
            await fetchMembers(); // Re-fetch members to update the list
            setIsAddModalOpen(false); // Close add modal
            setIsEditModalOpen(false); // Close edit modal
            setMemberToEdit(null); // Clear member being edited
            // Optionally show a global success message/toast
            // alert(`Member ${memberId ? 'updated' : 'added'} successfully!`);
        } catch (err: any) {
            console.error("Error saving member:", err);
            // Throw the error so it can be caught by the modal's internal error state
            throw err;
        }
    };

    // Handles confirmation for deletion
    const handleDeleteConfirm = async () => {
        if (!memberToDelete) return; // Should not happen if modal is opened correctly

        try {
            await api.delete(`/members/${memberToDelete._id}`);
            await fetchMembers(); // Re-fetch members to update the list
            setIsDeleteModalOpen(false); // Close delete confirmation modal
            setMemberToDelete(null); // Clear member being deleted
            // alert('Member deleted successfully!'); // Optionally show a global success message/toast
        } catch (err: any) {
            console.error("Error deleting member:", err);
            setError(err.response?.data?.message || err.message || 'Failed to delete member.');
        }
    };

    // --- Modal Open/Close handlers ---
    const openAddModal = () => setIsAddModalOpen(true);
    const closeAddModal = () => setIsAddModalOpen(false);

    const openEditModal = (member: Member) => {
        setMemberToEdit(member);
        setIsEditModalOpen(true);
    };
    const closeEditModal = () => {
        setMemberToEdit(null);
        setIsEditModalOpen(false);
    };

    const openDeleteModal = (member: Member) => {
        setMemberToDelete(member);
        setIsDeleteModalOpen(true);
    };
    const closeDeleteModal = () => {
        setMemberToDelete(null);
        setIsDeleteModalOpen(false);
    };

    // --- Conditional rendering for permissions ---
    // User roles should align with your backend authorization
    const canAddEditMembers = user && (user.role === 'admin' || user.role === 'super-admin');
    const canDeleteMembers = user && user.role === 'super-admin'// Only super-admin can delete

    if (loading) {
        return (
            <div className="flex justify-center items-center h-48">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
                <p className="ml-4 text-gray-700">Loading members...</p>
            </div>
        );
    }

    // Display a prominent error if data couldn't be loaded at all
    if (error && !members.length) {
        return (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                <strong className="font-bold">Error!</strong>
                <span className="block sm:inline ml-2">{error}</span>
            </div>
        );
    }

    return (
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md min-h-[calc(100vh-120px)] flex flex-col">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6">
                <h2 className="text-2xl sm:text-3xl font-semibold text-gray-800 mb-4 sm:mb-0">Manage Church Members</h2>
                {canAddEditMembers && (
                    <button
                        onClick={openAddModal}
                        className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition duration-150 ease-in-out"
                    >
                        Add New Member
                    </button>
                )}
            </div>

            {/* Display error as a banner if some members loaded, but an operation failed */}
            {error && members.length > 0 && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
                    <strong className="font-bold">Error:</strong>
                    <span className="block sm:inline ml-2">{error}</span>
                </div>
            )}

            {members.length === 0 && !loading ? (
                <p className="text-gray-600 text-lg text-center mt-10">No members found for this church.</p>
            ) : (
                <>
                    <div className="overflow-x-auto flex-grow">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Name
                                    </th>
                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Email
                                    </th>
                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                                        Primary Phone
                                    </th>
                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                                        Secondary Phone
                                    </th>
                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                                        Address
                                    </th>
                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                                        Gender
                                    </th>
                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                                        Date of Birth
                                    </th>
                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Membership Status
                                    </th>
                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                                        Role
                                    </th>
                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                                        Status
                                    </th>
                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                                        Date Joined
                                    </th>
                                    {(canAddEditMembers || canDeleteMembers) && ( // Only show Actions column if user has permission
                                        <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {members.map((member) => (
                                    <tr key={member._id}>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {member.firstName} {member.lastName}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {member.email}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 hidden sm:table-cell">
                                            {member.phoneNumber || 'N/A'}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
                                            {member.phoneNumber2 || 'N/A'} {/* Display phoneNumber2 */}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 hidden lg:table-cell">
                                            {member.address || 'N/A'}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 capitalize hidden sm:table-cell">
                                            {member.gender || 'N/A'}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
                                            {member.dateOfBirth ? new Date(member.dateOfBirth).toLocaleDateString() : 'N/A'}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {member.membershipStatus || 'N/A'} {/* Display membershipStatus */}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 capitalize hidden lg:table-cell">
                                            {member.role}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 hidden sm:table-cell">
                                            {member.isActive ? (
                                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                                    Active
                                                </span>
                                            ) : (
                                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                                    Inactive
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
                                            {new Date(member.registrationDate).toLocaleDateString()}
                                        </td>
                                        {(canAddEditMembers || canDeleteMembers) && (
                                            <td className="px-4 py-4 whitespace-nowrap text-center text-sm font-medium">
                                                {canAddEditMembers && (
                                                    <button
                                                        onClick={() => openEditModal(member)}
                                                        className="text-indigo-600 hover:text-indigo-900 mr-2 sm:mr-4"
                                                        title="Edit Member"
                                                    >
                                                        Edit
                                                    </button>
                                                )}
                                                {canDeleteMembers && (
                                                    // Ensure a super-admin cannot delete themselves if that's a requirement
                                                    member._id !== user?._id && (
                                                        <button
                                                            onClick={() => openDeleteModal(member)}
                                                            className="text-red-600 hover:text-red-900"
                                                            title="Delete Member"
                                                        >
                                                            Delete
                                                        </button>
                                                    )
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    <div className="mt-6 flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
                        <button
                            onClick={handlePrevPage}
                            disabled={currentPage === 1 || loading}
                            className="w-full sm:w-auto px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Previous
                        </button>
                        <span className="text-gray-700 text-sm sm:text-base">
                            Page {currentPage} of {totalPages} ({totalMembers} members)
                        </span>
                        <button
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages || loading}
                            className="w-full sm:w-auto px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                        </button>
                    </div>
                </>
            )}

            {/* Member Add/Edit Modal */}
            <MemberFormModal
                isOpen={isAddModalOpen || isEditModalOpen}
                onClose={isAddModalOpen ? closeAddModal : closeEditModal}
                onSave={handleSaveMember}
                memberToEdit={memberToEdit}
            />

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={closeDeleteModal}
                onConfirm={handleDeleteConfirm}
                message={`Are you sure you want to delete member: ${memberToDelete?.firstName} ${memberToDelete?.lastName}? This action cannot be undone.`}
                confirmText="Delete"
                isDestructive={true}
            />
        </div>
    );
};

